// Two-phase validation (form-view slice): blur checks format only —
// emptiness is never flagged until a submit attempt marks every missing required
// field. Mirrors the design reference's `validateValue` plus the per-field
// `Validation` regex the production definition carries.

import { CfRegistry } from './formsConfig';
import { CfState } from './formsReducer';
import { isStructurallyComplete, statusOf, visibleFields } from './formsSelectors';
import { CfField, CfForm, FieldValue } from './types';
import { asInstances, isEmptyValue } from './valueFormat';

export const REQUIRED_MESSAGE = 'Required — needed to submit.';

const DATE_RE = /^(\d{2})\/(\d{2})\/(\d{4})$/;
const MONTHYEAR_RE = /^(\d{2})\/(\d{4})$/;
const EMAIL_RE = /^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i;

const validDate = (text: string): boolean => {
    const m = text.match(DATE_RE);
    if (!m) return false;
    const month = Number(m[1]);
    const day = Number(m[2]);
    return month >= 1 && month <= 12 && day >= 1 && day <= 31;
};

const validMonthYear = (text: string): boolean => {
    const m = text.match(MONTHYEAR_RE);
    if (!m) return false;
    const month = Number(m[1]);
    return month >= 1 && month <= 12;
};

/**
 * Format-only check for a present value; `null` means the value passes. An
 * empty value always passes here — blur never flags emptiness.
 */
/** Word counting, shared with the long-text control's live counter. */
export const countWords = (text: string): number =>
    text.trim() ? text.trim().split(/\s+/).length : 0;

export const validateFormat = (field: CfField, value: FieldValue | undefined | null): string | null => {
    if (isEmptyValue(value)) return null;
    // Structural completeness is not a format concern (blur never flags a
    // partial group or consent), and stringifying instances is meaningless.
    if (field.type === 'group' || field.type === 'consent') return null;
    const text = String(value);

    switch (field.type) {
        case 'longtext':
            if (field.maxWords && countWords(text) > field.maxWords) {
                return `Keep it under ${field.maxWords} words.`;
            }
            break;
        case 'email':
            if (!EMAIL_RE.test(text)) return 'Enter a valid email address.';
            break;
        case 'date':
            if (!validDate(text)) return 'Enter a date as MM/DD/YYYY.';
            break;
        case 'monthyear':
            if (!validMonthYear(text)) return 'Enter a month as MM/YYYY.';
            break;
        case 'daterange': {
            const [from, to] = text.split('|');
            if (!validMonthYear(from ?? '') || !validMonthYear(to ?? '')) {
                return 'Enter both months as MM/YYYY.';
            }
            break;
        }
        case 'zip':
            if (!/^\d{5}$/.test(text)) return 'Enter a 5-digit ZIP code.';
            break;
        case 'phone':
            if (text.replace(/\D/g, '').length !== 10) return 'Enter a 10-digit phone number.';
            break;
        default:
            break;
    }

    if (field.validation) {
        try {
            if (!new RegExp(field.validation).test(text)) {
                return `Enter a valid ${field.label.toLowerCase()}.`;
            }
        } catch (_) {
            // A malformed tenant regex never blocks the student.
        }
    }

    return null;
};

/**
 * Submit-time message for a structurally incomplete multi-part value (the
 * status machine already withholds `confirmed`; this is the copy a submit
 * attempt lands on the row). Prototype wording. An empty value is the
 * required-marking path's business, not this one's.
 */
export const structuralError = (field: CfField, value: FieldValue | undefined | null): string | null => {
    if (isEmptyValue(value)) return null;
    if (field.type === 'consent' && !isStructurallyComplete(field, value as FieldValue)) {
        return 'All certifications must be checked to submit.';
    }
    if (field.type === 'group') {
        const required = (field.template ?? []).filter((sub) => !sub.optional);
        const unfinished = asInstances(value as FieldValue).some((instance) =>
            required.some((sub) => isEmptyValue(instance[sub.id]))
        );
        if (unfinished) {
            return `Complete or remove the unfinished ${(field.itemLabel || 'entry').toLowerCase()}.`;
        }
    }
    return null;
};

export type SubmitValidation = {
    /** Every failure found, keyed by field id — required gaps and format breaks. */
    errors: Record<string, string>;
    /** The first blocked field in form order — the scroll target. */
    firstBlockedId: string | null;
    /** Count shown in the end-card line ("{n} required answers still needed"). */
    blockedCount: number;
};

/**
 * Submit-time validation: every visible required field must hold a valid
 * committed value. Pending suggestions count as blocked (they need a confirm)
 * but keep their suggested treatment — no error message lands on them. Hidden
 * conditional fields — including cross-form-parented ones, which is why the
 * registry rides along — never block, required or not.
 */
export const validateForSubmit = (
    form: CfForm,
    state: CfState,
    registry?: CfRegistry | null
): SubmitValidation => {
    const errors: Record<string, string> = {};
    let firstBlockedId: string | null = null;
    let blockedCount = 0;

    visibleFields(form, state, registry).forEach((field) => {
        const value = state.values[field.id];
        const formatError = validateFormat(field, value) ?? structuralError(field, value);
        const status = statusOf(state, field.id, field);
        const missing = !field.optional && status !== 'confirmed';

        if (!formatError && !missing) return;
        blockedCount += 1;
        if (!firstBlockedId) firstBlockedId = field.id;
        if (formatError) {
            errors[field.id] = formatError;
        } else if (missing && status !== 'suggested') {
            errors[field.id] = REQUIRED_MESSAGE;
        }
    });

    return { errors, firstBlockedId, blockedCount };
};
